import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router";
import { useTextDomainContext, useUser } from "@/app/providers";
import { useQuery, useMutation } from "@apollo/client/react";
import { queries, mutations } from "@/graphql";
import { enqueueSnackbar } from "notistack";
import { getPartnerKey, getPartnerName } from "@/utils";
import { useGetPartnerByKey, usePageTitle, usePartnersList } from "@/hooks";

const PARTNER_YES_NO_OPTIONS = ["Default", "Yes", "No"];

export interface PartnerSettingItemDef {
  key: string;
  label: string;
  description: string;
  type: "default-yes-no" | "yes-no";
  category: "registration";
  defaultValue: string;
}

const getPartnerSettingItems = (
  gettext: (s: string) => string,
): PartnerSettingItemDef[] => [
  {
    key: "allow-registration",
    label: gettext("Allow Registration"),
    description: gettext(
      "Control whether users can register / sign up for new accounts on this partner portal.",
    ),
    type: "default-yes-no",
    category: "registration",
    defaultValue: "Default",
  },
];

const PartnerSettingCard = ({
  item,
  currentValue,
  savingKey,
  partnerId,
  partnerKey,
  onValueChange,
  onSaveSingle,
}: {
  item: PartnerSettingItemDef;
  currentValue: any;
  savingKey: string | null;
  partnerId?: string;
  partnerKey?: string;
  onValueChange: (key: string, val: any) => void;
  onSaveSingle: (key: string, label: string, val: any) => void;
}) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const { data, loading } = useQuery<{ partnerSettings: any }>(
    queries.partnerSettings,
    {
      variables: {
        partnerId: partnerId || undefined,
        partnerKey: partnerKey || undefined,
        key: item.key,
        fallback: false,
      },
      skip: !partnerId && !partnerKey,
      errorPolicy: "all",
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    if (data !== undefined) {
      let raw = data?.partnerSettings;
      if (raw !== null && raw !== undefined) {
        if (typeof raw === "string") {
          try {
            const dec = JSON.parse(raw);
            if (typeof dec === "boolean") {
              raw = dec ? "Yes" : "No";
            } else if (typeof dec === "string") {
              raw = dec;
            }
          } catch {
            // raw is regular string
          }
        } else if (typeof raw === "boolean") {
          raw = raw ? "Yes" : "No";
        }

        if (
          typeof raw === "string" &&
          PARTNER_YES_NO_OPTIONS.some(
            (opt) => opt.toLowerCase() === raw.toLowerCase(),
          )
        ) {
          const matched =
            PARTNER_YES_NO_OPTIONS.find(
              (opt) => opt.toLowerCase() === raw.toLowerCase(),
            ) || raw;
          onValueChange(item.key, matched);
          return;
        }
      }
      onValueChange(item.key, item.defaultValue);
    }
  }, [data, partnerId]);

  const displayVal = currentValue ?? item.defaultValue;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          {item.label}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          {item.description}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id={`partner-select-label-${item.key}`}>
              {item.label}
            </InputLabel>
            <Select
              labelId={`partner-select-label-${item.key}`}
              value={displayVal}
              label={item.label}
              onChange={(e) => onValueChange(item.key, e.target.value)}
            >
              {PARTNER_YES_NO_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {gettext(opt)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </CardContent>
      <Box sx={{ p: 2, pt: 0, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          size="small"
          disabled={savingKey === item.key || loading}
          onClick={() => onSaveSingle(item.key, item.label, displayVal)}
        >
          {savingKey === item.key ? (
            <CircularProgress size={16} />
          ) : (
            gettext("Save")
          )}
        </Button>
      </Box>
    </Card>
  );
};

export const PartnerSettings = ({
  partnerId: propPartnerId,
}: {
  partnerId?: string;
} = {}) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const navigate = useNavigate();
  const params = useParams<{ partnerId?: string }>();
  const partnerIdFromRoute = propPartnerId || params.partnerId;

  usePageTitle(gettext("Partner Settings"));

  const urlPartnerKey = getPartnerKey();
  const { partnerList } = usePartnersList();
  const routePartner = partnerList.find((p) => p.id === partnerIdFromRoute);
  const { partner: urlPartner } = useGetPartnerByKey(urlPartnerKey);

  const effectivePartnerId =
    partnerIdFromRoute ||
    user?.partner?.id ||
    user?.currentPartnerId ||
    urlPartner?.id;
  const effectivePartnerKey =
    routePartner?.key ||
    (partnerIdFromRoute ? undefined : user?.partner?.key || urlPartnerKey);
  const effectivePartnerName =
    routePartner?.name ||
    user?.partner?.name ||
    urlPartner?.name ||
    getPartnerName();

  const settingItems = getPartnerSettingItems(gettext);

  const [settingsValues, setSettingsValues] = useState<Record<string, any>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveAllLoading, setSaveAllLoading] = useState<boolean>(false);

  const [updatePartnerSettings] = useMutation<any>(
    mutations.updatePartnerSettings,
    {
      errorPolicy: "all",
    },
  );

  const handleValueChange = (key: string, val: any) => {
    setSettingsValues((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const saveSingleSetting = async (
    key: string,
    label: string,
    valOverride?: any,
  ) => {
    const val = valOverride ?? settingsValues[key];
    setSavingKey(key);
    try {
      const res: any = await updatePartnerSettings({
        variables: {
          partnerId: effectivePartnerId || undefined,
          key,
          data: JSON.stringify(val),
        },
        refetchQueries: ["PartnerSettings"],
      });
      const result = res.data?.updatePartnerSettings;
      if (result?.success) {
        enqueueSnackbar(gettext("Partner setting updated successfully!"), {
          variant: "success",
        });
      } else {
        const errorMsg =
          result?.message?.[0] ||
          res.errors?.[0]?.message ||
          gettext("Failed to save partner setting.");
        enqueueSnackbar(errorMsg, { variant: "error" });
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || gettext("An error occurred"), {
        variant: "error",
      });
    } finally {
      setSavingKey(null);
    }
  };

  const handleSaveAll = async () => {
    setSaveAllLoading(true);
    try {
      for (let i = 0; i < settingItems.length; i++) {
        const item = settingItems[i];
        const val = settingsValues[item.key] ?? item.defaultValue;
        const isLast = i === settingItems.length - 1;
        await updatePartnerSettings({
          variables: {
            partnerId: effectivePartnerId || undefined,
            key: item.key,
            data: JSON.stringify(val),
          },
          ...(isLast ? { refetchQueries: ["PartnerSettings"] } : {}),
        });
      }
      enqueueSnackbar(gettext("All partner settings saved successfully!"), {
        variant: "success",
      });
    } catch (err: any) {
      enqueueSnackbar(err.message || gettext("Failed to save settings"), {
        variant: "error",
      });
    } finally {
      setSaveAllLoading(false);
    }
  };

  const registrationSettings = settingItems.filter(
    (i) => i.category === "registration",
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            {gettext("Partner Settings")}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {effectivePartnerName
              ? `${gettext("Manage settings for")} ${effectivePartnerName}.`
              : gettext("Manage settings for your partner portal.")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          {partnerIdFromRoute && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              {gettext("Back")}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={
              saveAllLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSaveAll}
            disabled={saveAllLoading}
          >
            {gettext("Save All Settings")}
          </Button>
        </Stack>
      </Box>

      {/* Registration Settings Section */}
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        {gettext("Registration Settings")}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {gettext(
          "Configure whether users can register or sign up directly on this partner's domain.",
        )}
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {registrationSettings.map((item) => (
          <Grid size={{ xs: 12, md: 4 }} key={item.key}>
            <PartnerSettingCard
              item={item}
              currentValue={settingsValues[item.key]}
              savingKey={savingKey}
              partnerId={effectivePartnerId}
              partnerKey={effectivePartnerKey}
              onValueChange={handleValueChange}
              onSaveSingle={saveSingleSetting}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
